import { Variants } from "framer-motion";
import { ANIMATION_CONFIG, ANIMATION_STATE } from "./constants";
import {
  calculateEnterTimes,
  calculatePulseLoopTimes,
  getExitTransition,
  getFadeInTransition,
} from "./helpers";

// --- REUSABLE VALUES FOR COIN ANIMATIONS ---
const {
  ENTER_DURATION,
  PULSE_CYCLE_DURATION,
  COIN_Y_START,
  COIN_Y_END,
  COIN_SCALE_START,
  PULSE_SCALE_SMALL,
  PULSE_SCALE_BIG,
  PULSE_X_SHIFT_IN,
  PULSE_Y_SHIFT_IN,
  PULSE_X_SHIFT_OUT,
  PULSE_Y_SHIFT_OUT,
  BONUS_TEXT_Y_SHIFT_OUT,
  BASE_DELAY,
  PULSE_DURATION_OUT,
} = ANIMATION_CONFIG;

// --- Keyframes for Coins (Full original keyframes) ---
const yKeyframes = [
  COIN_Y_START, // 0
  COIN_Y_END, // 1
  COIN_Y_END, // 2
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 3
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 4
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 5
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 6
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 7
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 8
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 9
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 10
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 11
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 12
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 13
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 14
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 15
  COIN_Y_END - PULSE_Y_SHIFT_IN, // 16
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 17
  COIN_Y_END - PULSE_Y_SHIFT_OUT, // 18
];
const scaleKeyframes = [
  COIN_SCALE_START, // 0
  1, // 1
  1, // 2
  PULSE_SCALE_SMALL, // 3
  PULSE_SCALE_SMALL, // 4
  PULSE_SCALE_BIG, // 5
  PULSE_SCALE_BIG, // 6
  PULSE_SCALE_SMALL, // 7
  PULSE_SCALE_SMALL, // 8
  PULSE_SCALE_BIG, // 9
  PULSE_SCALE_BIG, // 10
  PULSE_SCALE_SMALL, // 11
  PULSE_SCALE_SMALL, // 12
  PULSE_SCALE_BIG, // 13
  PULSE_SCALE_BIG, // 14
  PULSE_SCALE_SMALL, // 15
  PULSE_SCALE_SMALL, // 16
  PULSE_SCALE_BIG, // 17
  PULSE_SCALE_BIG, // 18
];
const xKeyframesLeft = [
  0, // 0
  0, // 1
  0, // 2
  PULSE_X_SHIFT_IN, // 3
  PULSE_X_SHIFT_IN, // 4
  -PULSE_X_SHIFT_OUT, // 5
  -PULSE_X_SHIFT_OUT, // 6
  PULSE_X_SHIFT_IN, // 7
  PULSE_X_SHIFT_IN, // 8
  -PULSE_X_SHIFT_OUT, // 9
  -PULSE_X_SHIFT_OUT, // 10
  PULSE_X_SHIFT_IN, // 11
  PULSE_X_SHIFT_IN, // 12
  -PULSE_X_SHIFT_OUT, // 13
  -PULSE_X_SHIFT_OUT, // 14
  PULSE_X_SHIFT_IN, // 15
  PULSE_X_SHIFT_IN, // 16
  0, // 17
  0, // 18
];
const xKeyframesRight = [
  0, // 0
  0, // 1
  0, // 2
  -PULSE_X_SHIFT_IN, // 3
  -PULSE_X_SHIFT_IN, // 4
  PULSE_X_SHIFT_OUT, // 5
  PULSE_X_SHIFT_OUT, // 6
  -PULSE_X_SHIFT_IN, // 7
  -PULSE_X_SHIFT_IN, // 8
  PULSE_X_SHIFT_OUT, // 9
  PULSE_X_SHIFT_OUT, // 10
  -PULSE_X_SHIFT_IN, // 11
  -PULSE_X_SHIFT_IN, // 12
  PULSE_X_SHIFT_OUT, // 13
  PULSE_X_SHIFT_OUT, // 14
  -PULSE_X_SHIFT_IN, // 15
  -PULSE_X_SHIFT_IN, // 16
  0, // 17
  0, // 18
];

// --- Keyframes for Bonus Text (Full original keyframes) ---
const TEXT_Y_BASE = -8;
const TEXT_Y_UP_SMALL = TEXT_Y_BASE;
const TEXT_Y_UP_BIG = TEXT_Y_BASE - BONUS_TEXT_Y_SHIFT_OUT;

const yKeyframesBonusText = [
  -20, // 0
  TEXT_Y_BASE, // 1
  TEXT_Y_BASE, // 2
  TEXT_Y_BASE, // 3
  TEXT_Y_BASE, // 4
  TEXT_Y_UP_BIG, // 5
  TEXT_Y_UP_BIG, // 6
  TEXT_Y_UP_SMALL, // 7
  TEXT_Y_UP_SMALL, // 8
  TEXT_Y_UP_BIG, // 9
  TEXT_Y_UP_BIG, // 10
  TEXT_Y_UP_SMALL, // 11
  TEXT_Y_UP_SMALL, // 12
  TEXT_Y_UP_BIG, // 13
  TEXT_Y_UP_BIG, // 14
  TEXT_Y_UP_SMALL, // 15
  TEXT_Y_UP_SMALL, // 16
  TEXT_Y_BASE, // 17
  TEXT_Y_BASE, // 18
];
const opacityKeyframesBonusText = [
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
];

const opacityKeyframesAmountShine = [
  0, 0, 0, 0, 0, 1, 1, 0, 0, 1, 1, 0, 0, 1, 1, 0, 0, 0, 0,
];

// --- Sliced keyframes for PULSING state (Looping Frames 3-7) ---
// These create a 5-element array: [idx 3, idx 4, idx 5, idx 6, idx 7]
const yKeyframes_PulseLoop = yKeyframes.slice(3, 8);
const scaleKeyframes_PulseLoop = scaleKeyframes.slice(3, 8);
const xKeyframesLeft_PulseLoop = xKeyframesLeft.slice(3, 8);
const xKeyframesRight_PulseLoop = xKeyframesRight.slice(3, 8);
const yKeyframesBonusText_PulseLoop = yKeyframesBonusText.slice(3, 8);
const opacityKeyframesBonusText_PulseLoop = opacityKeyframesBonusText.slice(3, 8);
const opacityKeyframesAmountShine_PulseLoop =
  opacityKeyframesAmountShine.slice(3, 8);
const pulseLoopTimes = calculatePulseLoopTimes(); // Get the times for this loop

// --- Sliced keyframes for ENTERING state (Frames 0-3) ---
// These create a 4-element array: [idx 0, idx 1, idx 2, idx 3]
const yKeyframes_Enter = yKeyframes.slice(0, 4);
const scaleKeyframes_Enter = scaleKeyframes.slice(0, 4);
const xKeyframesLeft_Enter = xKeyframesLeft.slice(0, 4);
const xKeyframesRight_Enter = xKeyframesRight.slice(0, 4);
const yKeyframesBonusText_Enter = yKeyframesBonusText.slice(0, 4);
const opacityKeyframesBonusText_Enter = opacityKeyframesBonusText.slice(0, 4);
const opacityKeyframesAmountShine_Enter =
  opacityKeyframesAmountShine.slice(0, 4);

// --- VARIANTS ---
export const borderVariant: Variants = {
  [ANIMATION_STATE.IDLE]: { opacity: 0 },
  [ANIMATION_STATE.ENTERING]: {
    opacity: 1,
    transition: getFadeInTransition(0),
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
    transition: getExitTransition(),
  },
};

export const bottomShadowVariant: Variants = {
  [ANIMATION_STATE.IDLE]: { opacity: 0 },
  [ANIMATION_STATE.ENTERING]: {
    opacity: 1,
    transition: getFadeInTransition(0),
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
    transition: getExitTransition(),
  },
};

const midCoinPulseStart = ENTER_DURATION + ENTER_DURATION * 2 + ENTER_DURATION;
const leftCoinPulseStart = ENTER_DURATION + ENTER_DURATION + ENTER_DURATION;
const rightCoinPulseStart = ENTER_DURATION + ENTER_DURATION;
const textCoinPulseStart = ENTER_DURATION;

export const midCoinVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
  },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter,
    y: yKeyframes_Enter,
    scale: scaleKeyframes_Enter,
    transition: {
      delay: BASE_DELAY,
      ease: "linear",
      duration: midCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        midCoinPulseStart + PULSE_DURATION_OUT,
        midCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: 1,
    y: yKeyframes_PulseLoop,
    scale: scaleKeyframes_PulseLoop,
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
    transition: getExitTransition(),
  },
};

export const leftCoinVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
  },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter,
    y: yKeyframes_Enter,
    scale: scaleKeyframes_Enter,
    x: xKeyframesLeft_Enter,
    transition: {
      delay: BASE_DELAY + ENTER_DURATION,
      ease: "linear",
      duration: leftCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        leftCoinPulseStart + PULSE_DURATION_OUT,
        leftCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: 1,
    y: yKeyframes_PulseLoop,
    scale: scaleKeyframes_PulseLoop,
    x: xKeyframesLeft_PulseLoop,
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
    transition: getExitTransition(),
  },
};

export const amountVariant: Variants = {
  [ANIMATION_STATE.IDLE]: { opacity: 0 },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter,
    transition: {
      delay: BASE_DELAY + ENTER_DURATION * 3,
      ease: "linear",
      duration: textCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        textCoinPulseStart + PULSE_DURATION_OUT,
        textCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: opacityKeyframesBonusText_PulseLoop,
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
    transition: getExitTransition(),
  },
};

export const amountShineVariant: Variants = {
  [ANIMATION_STATE.IDLE]: { opacity: 0 },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesAmountShine_Enter,
    transition: {
      delay: BASE_DELAY + ENTER_DURATION * 3,
      ease: "linear",
      duration: textCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        textCoinPulseStart + PULSE_DURATION_OUT,
        textCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: opacityKeyframesAmountShine_PulseLoop,
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
  },
};

export const amountShadowVariant: Variants = {
  [ANIMATION_STATE.IDLE]: { opacity: 0 },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter, // Syncs with amount
    transition: {
      delay: BASE_DELAY + ENTER_DURATION * 3,
      ease: "linear",
      duration: textCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        textCoinPulseStart + PULSE_DURATION_OUT,
        textCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: opacityKeyframesBonusText_PulseLoop, // Syncs with amount
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
    transition: getExitTransition(),
  },
};

export const rightCoinVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
  },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter,
    y: yKeyframes_Enter,
    scale: scaleKeyframes_Enter,
    x: xKeyframesRight_Enter,
    transition: {
      delay: BASE_DELAY + ENTER_DURATION * 2,
      ease: "linear",
      duration: rightCoinPulseStart + PULSE_DURATION_OUT,
      times: calculateEnterTimes(
        rightCoinPulseStart + PULSE_DURATION_OUT,
        rightCoinPulseStart
      ),
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: 1,
    y: yKeyframes_PulseLoop,
    scale: scaleKeyframes_PulseLoop,
    x: xKeyframesRight_PulseLoop,
    transition: {
      ease: "linear",
      duration: PULSE_CYCLE_DURATION,
      times: pulseLoopTimes,
      repeat: Infinity,
      repeatType: "loop",
    },
  },
  exit: {
    opacity: 0,
    y: COIN_Y_START,
    scale: COIN_SCALE_START,
    transition: getExitTransition(),
  },
};

export const bonusTextVariant: Variants = {
  [ANIMATION_STATE.IDLE]: {
    opacity: 0,
    y: -20,
    x: "-50%",
    left: "50%",
    "--timer": 0,
  },
  [ANIMATION_STATE.ENTERING]: {
    opacity: opacityKeyframesBonusText_Enter,
    y: yKeyframesBonusText_Enter,
    x: "-50%",
    left: "50%",

    transition: {
      opacity: {
        delay: BASE_DELAY + ENTER_DURATION * 3,
        ease: "linear",
        duration: textCoinPulseStart + PULSE_DURATION_OUT,
        times: calculateEnterTimes(
          textCoinPulseStart + PULSE_DURATION_OUT,
          textCoinPulseStart
        ),
      },
      y: {
        delay: BASE_DELAY + ENTER_DURATION * 3,
        ease: "linear",
        duration: textCoinPulseStart + PULSE_DURATION_OUT,
        times: calculateEnterTimes(
          textCoinPulseStart + PULSE_DURATION_OUT,
          textCoinPulseStart
        ),
      },
    },
  },
  [ANIMATION_STATE.PULSING]: {
    opacity: opacityKeyframesBonusText_PulseLoop,
    y: yKeyframesBonusText_PulseLoop,
    x: "-50%",
    left: "50%",
    "--timer": 1,

    transition: {
      opacity: {
        ease: "linear",
        duration: PULSE_CYCLE_DURATION,
        times: pulseLoopTimes,
        repeat: Infinity,
        repeatType: "loop",
      },
      y: {
        ease: "linear",
        duration: PULSE_CYCLE_DURATION,
        times: pulseLoopTimes,
        repeat: Infinity,
        repeatType: "loop",
      },
      "--timer": {
        duration: PULSE_CYCLE_DURATION,
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop",
      },
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    x: "-50%",
    left: "50%",
    transition: getExitTransition(),
  },
};
